import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Dashboard from "./views/Dashboard";
import Maps from "./views/Maps";
import Courses from "./views/Courses";
import Login from "./views/Login";
import Landing from "./views/Landing";
import PlanSelection from "./views/PlanSelection";
import { Fragment, useEffect } from "react";
import Navigation from "./layout/Navigation";
import { useContext } from "react";
import { Context } from "./context/AuthContext";
import Subscribe from "./views/Subscribe";
import useWebSocketStore from "./services/webSocket.service";
import { Conversation } from "./services/chat.service";
import Conversations from "./components/controls/Conversations/Conversations";
import RedeemCode from "./views/redeemFlow/RedeemCode";
import AvlTest from "./views/AvlTest";
import { AppState } from "./appState";
import CancelSubscription from "./views/CancelSubscription";
import FFFAPI from "./services/fffAPI.service";

const AppRouter = () => {
  const user = useContext(Context);
  const connect = useWebSocketStore((state: any) => state.connect);

  const showNavigation = AppState((state) => state.showNavigation);
  const showConversations = AppState((state) => state.showConversations);

  let socket: any;

  // Capture AvantLink affiliate click token on landing
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const avad = params.get("avad");
    const discountCode = params.get("discountcode");
    const sessionId = params.get("session_id");
    const path = window.location.pathname.replace(/\/$/, ""); // strip trailing slash

    if (avad) {
      // Generate a stable visitor ID for AvantLink S2S tracking
      const visitorId = crypto.randomUUID();
      localStorage.setItem("avantlink_avad", avad);
      localStorage.setItem("avantlink_visitor_id", visitorId);
      localStorage.setItem("avantlink_referrer", document.referrer);
      localStorage.setItem("avantlink_landing_url", window.location.href);
      FFFAPI.trackAffiliateClick({
        avantlink_avad: avad,
        visitor_id: visitorId,
        current_url: window.location.href,
        referrer_url: document.referrer,
        user_agent: navigator.userAgent,
      });
    }

    // AvantLink is configured with /redeem-code as the destination URL.
    // If there are no Stripe params (session_id), this is affiliate traffic — redirect to landing.
    if (path === "/redeem-code" && !sessionId && (avad || discountCode)) {
      const redirectParams = new URLSearchParams();
      if (avad) redirectParams.set("avad", avad);
      if (discountCode) redirectParams.set("discountcode", discountCode);
      window.location.replace(`/?${redirectParams.toString()}`);
    }
  }, []);

  useEffect(() => {
    if (!user?.user) return;

    user.user.getIdToken().then((result: string) => {
      localStorage.setItem("ffftoken", result);
      localStorage.setItem("fffuser", user.user.uid);
      if (!socket) {
        socket = connect(result, user.user.uid);
      }
    });

    // Refresh the token every 50 minutes so API calls never use an expired token
    const tokenRefreshInterval = setInterval(() => {
      user.user.getIdToken(true).then((result: string) => {
        localStorage.setItem("ffftoken", result);
      });
    }, 50 * 60 * 1000);

    return () => {
      clearInterval(tokenRefreshInterval);
      socket?.disconnect();
    };
  }, [connect, user?.user]);

  return (
    <Router>
      <Fragment>
        {!!user?.user && !!showNavigation && <Navigation />}
        {!!user?.user && !!showConversations && <Conversations />}
        <Routes>
          {/* Landing page - entry point for new visitors */}
          <Route path="/" element={<Landing />} />
          {/* Auth pages */}
          <Route path="/login" element={<Login />} />
          {/* Plan selection - shown after login/signup */}
          <Route path="/select-plan" element={<PlanSelection />} />
          {/* Redeem code flow (existing) */}
          <Route path="/redeem-code" element={<RedeemCode />} />
          <Route path="/cancel-subscription" element={<CancelSubscription />} />
          {/* TEMP: AvantLink S2S integration test — delete after testing */}
          <Route path="/avl-test" element={<AvlTest />} />
          {/* Catch-all: send to select-plan for authenticated, landing for unauthenticated */}
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      </Fragment>
    </Router>
  );
};

export default AppRouter;
